/**
 * VH-83: Recipe Library API client.
 *
 * Wraps /api/v1/recipes/* endpoints exposed by the backend recipes router.
 * All calls use the shared axios instance from `services/api.js` so
 * authentication, FormData handling and 401 redirects work uniformly.
 */

import { api } from './api'

// Equipment keys, in suggested UI display order. Mirrors backend constants.
export const EQUIPMENT_KEYS = [
  'stovetop',
  'oven',
  'microwave',
  'blender',
  'dough_mixer',
  'air_fryer',
  'pressure_cooker',
  'sous_vide',
  'teppanyaki',
]

export const recipesAPI = {
  // -- Library --------------------------------------------------------
  list: (params) => api.get('/api/v1/recipes/', { params }),
  get: (id) => api.get(`/api/v1/recipes/${id}`),
  delete: (id) => api.delete(`/api/v1/recipes/${id}`),

  // -- Capture --------------------------------------------------------
  create: (data) => api.post('/api/v1/recipes/', data),
  aiGenerate: (data) => api.post('/api/v1/recipes/ai-generate', data),
  fromPhoto: (formData) =>
    api.post('/api/v1/recipes/from-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // -- Versions -------------------------------------------------------
  listVersions: (recipeId) => api.get(`/api/v1/recipes/${recipeId}/versions`),
  modifyVersion: (recipeId, versionId, data) =>
    api.put(`/api/v1/recipes/${recipeId}/versions/${versionId}`, data),
  deleteVersion: (recipeId, versionId) =>
    api.delete(`/api/v1/recipes/${recipeId}/versions/${versionId}`),
  fork: (recipeId, data) => api.post(`/api/v1/recipes/${recipeId}/fork`, data),
  switchCurrentVersion: (recipeId, versionId) =>
    api.patch(`/api/v1/recipes/${recipeId}/current-version`, {
      version_id: versionId,
    }),

  // -- Nutrition (VH-236) ---------------------------------------------
  // Match / estimate / type the ingredient values, then save; the server does all arithmetic.
  getNutrition: (recipeId, versionId = null) =>
    api.get(`/api/v1/recipes/${recipeId}/nutrition`, versionId ? { params: { version_id: versionId } } : undefined),
  estimateIngredientNutrition: (recipeId, ingredientId, versionId = null) =>
    api.post(`/api/v1/recipes/${recipeId}/nutrition/estimate`, { ingredient_id: ingredientId },
      versionId ? { params: { version_id: versionId } } : undefined),
  // VH-237: the whole recipe in one press. Unlike estimate-all this recalculates rows that
  // already carry a value (a wrong food match is exactly what "missing only" cannot repair) and
  // proposes the finished weight. Nothing is stored until Save.
  calculateNutrition: (recipeId, versionId = null, signal = undefined) =>
    api.post(`/api/v1/recipes/${recipeId}/nutrition/calculate`, null, {
      ...(versionId ? { params: { version_id: versionId } } : {}),
      ...(signal ? { signal } : {}),
    }),
  saveNutrition: (recipeId, payload, versionId = null) =>
    api.put(`/api/v1/recipes/${recipeId}/nutrition`, payload,
      versionId ? { params: { version_id: versionId } } : undefined),

  // -- Translation ----------------------------------------------------
  translate: (recipeId, targetLanguage) =>
    api.post(`/api/v1/recipes/${recipeId}/translate`, null, {
      params: { target_language: targetLanguage },
    }),

  // -- AI Scaling (VH-107) --------------------------------------------
  // Pure suggestion — no DB write. Backend calls GPT-4o, returns a fully
  // adapted recipe (ingredients, steps, servings, prep/cook minutes,
  // reasoning) for the user to review + edit + confirm. Saving reuses /fork.
  scaleSuggest: (recipeId, target) =>
    api.post(`/api/v1/recipes/${recipeId}/scale-suggest`, { target }),

  // -- Categories -----------------------------------------------------
  listCategories: (params) =>
    api.get('/api/v1/recipes/categories', { params }),

  // -- Equipment (lives on /profile/equipment) ------------------------
  getEquipment: () => api.get('/api/v1/profile/equipment'),
  updateEquipment: (data) => api.put('/api/v1/profile/equipment', data),

  // -- Add to meal plan (VH-85) --------------------------------------
  // payload = { log_date, meal_type, servings_count }
  addToMealPlan: (recipeId, versionId, payload) =>
    api.post(
      `/api/v1/recipes/${recipeId}/versions/${versionId}/add-to-meal-plan`,
      payload
    ),

  // -- Media: cover image (VH-101) -----------------------------------
  uploadCoverImage: (recipeId, formData) =>
    api.post(`/api/v1/recipes/${recipeId}/cover-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteCoverImage: (recipeId) =>
    api.delete(`/api/v1/recipes/${recipeId}/cover-image`),

  // -- Media: video (VH-102) -----------------------------------------
  setVideoYoutube: (recipeId, youtubeUrl) =>
    api.put(`/api/v1/recipes/${recipeId}/video/youtube`, {
      youtube_url: youtubeUrl,
    }),
  uploadVideoFile: (recipeId, formData) =>
    api.post(`/api/v1/recipes/${recipeId}/video/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteVideo: (recipeId) =>
    api.delete(`/api/v1/recipes/${recipeId}/video`),
}

export default recipesAPI
